// components/ContactForm.js - Contact form with Redux integration and EmailJS
import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  alpha,
  Snackbar,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import {
  updateFormData,
  submitForm,
  submitFormSuccess,
  resetForm,
} from "../redux/store";
import emailjs from "@emailjs/browser";

const ContactForm = () => {
  const dispatch = useDispatch();
  const { formData, submitted, loading } = useSelector(
    (state) => state.contact,
  );
  // If you use eventCategories elsewhere, keep it; otherwise you can remove
  const { categories,  } = useSelector(
    (state) => state.eventCategories || {},
  );

  // Local state for validation errors and snackbar
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const eventTypes = [
    "Corporate Event",
    "Wedding",
    "Birthday Party",
    "Conference",
    "Product Launch",
    "Music Concert",
    "Other",
  ];

  // ---------- VALIDATION ----------
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) newErrors.name = "Name is required";
    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.eventType) newErrors.eventType = "Event type is required";
    if (!formData.date) newErrors.date = "Date is required";

    setErrors(newErrors);
    return newErrors; // 👈 return the object, not a boolean
  };

  // ---------- HANDLE CHANGE ----------
  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormData({ [name]: value }));

    // Clear that field's error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // ---------- HANDLE SUBMIT ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("1. Form submitted, validating...");

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      console.log("2. Validation errors:", validationErrors);
      // errors already set in validateForm()
      return;
    }

    console.log("3. No validation errors, dispatching submit action");
    dispatch(submitForm()); // sets loading = true

    try {
      console.log("4. Preparing to send email via EmailJS...");

      const SERVICE_ID = "service_ounsof8";
      const TEMPLATE_ID = "template_q4wa1ho";
      const PUBLIC_KEY = "qyKcgSHCThJmH8XMc";

      const templateParams = {
        to_email: "info@momentsia.com",
        to_name: "Elevate Events Team",
        from_name: formData.name.trim(),
        from_email: formData.email.trim(),
        from_phone: formData.phone?.trim() || "Not provided",
        event_type: formData.eventType,
        event_date: formData.date,
        guests: formData.guests?.toString().trim() || "Not specified",
        message: formData.message?.trim() || "No additional message provided",
        submitted_at: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      console.log("5. Template params:", templateParams);

      // EmailJS availability check
      if (typeof emailjs === "undefined") {
        throw new Error(
          "EmailJS library not loaded. Make sure you've added the script tag or imported the package.",
        );
      }
      if (typeof emailjs.send !== "function") {
        throw new Error(
          "emailjs.send is not a function. Check your EmailJS initialization.",
        );
      }

      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY,
      );

      console.log("6. ✅ Email sent successfully!", response);
      console.log("Form data:", formData);

      // ----- SUCCESS -----
      dispatch(submitFormSuccess()); // sets loading = false
      setSnackbarOpen(true); // show success message

      // ✅ RESET FORM – using Redux action (clears all fields)
      dispatch(resetForm());
      setErrors({}); // clear validation errors

      // Optional: clear selected category if you have it
      // if () dispatch(selectCategory(null));
    } catch (error) {
      console.error("7. ❌ EmailJS error:", {
        error,
        name: error.name,
        message: error.message,
        text: error.text,
        status: error.status,
        stack: error.stack,
      });

      dispatch(submitFormSuccess()); // also stop loading on error

      // ----- FALLBACK: mailto link -----
      const mailtoLink = `mailto:info@momentsia.com?subject=Event Inquiry: ${encodeURIComponent(
        formData.eventType,
      )} - ${encodeURIComponent(formData.name)}&body=Event Details:%0D%0A%0D%0AName: ${encodeURIComponent(
        formData.name,
      )}%0D%0AEmail: ${encodeURIComponent(
        formData.email,
      )}%0D%0APhone: ${encodeURIComponent(
        formData.phone || "",
      )}%0D%0AEvent Type: ${encodeURIComponent(
        formData.eventType,
      )}%0D%0AEvent Date: ${encodeURIComponent(
        formData.date,
      )}%0D%0AGuests: ${encodeURIComponent(
        formData.guests || "",
      )}%0D%0A%0D%0AAdditional Details:%0D%0A${encodeURIComponent(
        formData.message || "",
      )}%0D%0A%0D%0A---%0D%0ASubmitted via Elevate Events Contact Form`;

      if (
        window.confirm(
          "Email service failed. Would you like to open your email client to send the inquiry?",
        )
      ) {
        window.location.href = mailtoLink;
      }
    }
  };

  // ---------- RESET AFTER SUCCESS VIEW ----------
  const handleReset = () => {
    dispatch(resetForm());
    setErrors({});
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // ---------- RENDER ----------
  return (
    <Box id="contact" sx={{ py: 8 }}>
      <Typography
        variant="h2"
        align="center"
        sx={{
          mb: 2,
          background: "linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        Plan Your Perfect Event
      </Typography>
      <Typography
        variant="h6"
        align="center"
        color="text.secondary"
        sx={{ mb: 6, maxWidth: 700, mx: "auto" }}
      >
        Share your event details with us, and we'll create a customized proposal
        within 24 hours
      </Typography>

      <Grid container spacing={6}>
        <Grid item xs={12} md={7}>
          <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 3 }}>
            {submitted ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    bgcolor: alpha("#00b09b", 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  <SendIcon sx={{ fontSize: 40, color: "#00b09b" }} />
                </Box>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  Thank You!
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, maxWidth: 500, mx: "auto" }}
                >
                  Your event inquiry has been received. Our team will review
                  your requirements and contact you within 24 hours to discuss
                  your event in detail.
                </Typography>
                <Button variant="outlined" onClick={handleReset} sx={{ px: 4 }}>
                  Submit Another Inquiry
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={formData.name || ""}
                      onChange={handleChange}
                      error={!!errors.name}
                      helperText={errors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email || ""}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={formData.phone || ""}
                      onChange={handleChange}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      label="Event Type"
                      name="eventType"
                      value={formData.eventType || ""}
                      onChange={handleChange}
                      error={!!errors.eventType}
                      helperText={errors.eventType}
                      required
                    >
                      {eventTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Event Date"
                      name="date"
                      type="date"
                      value={formData.date || ""}
                      onChange={handleChange}
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.date}
                      helperText={errors.date}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Approximate Guests"
                      name="guests"
                      type="number"
                      value={formData.guests || ""}
                      onChange={handleChange}
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Tell us about your event"
                      name="message"
                      multiline
                      rows={4}
                      value={formData.message || ""}
                      onChange={handleChange}
                      placeholder="Please share any specific requirements, themes, or ideas you have for your event..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      endIcon={
                        loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <SendIcon />
                        )
                      }
                      disabled={loading}
                      sx={{ px: 5, py: 1.5, borderRadius: 2 }}
                    >
                      {loading ? "Submitting..." : "Submit Inquiry"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Why Choose Elevate Events?
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              With over a decade of experience, we've mastered the art of
              creating unforgettable events. Our comprehensive approach ensures
              every detail is perfect.
            </Typography>

            {[
              {
                title: "Personalized Planning",
                desc: "Customized solutions tailored to your vision and budget",
              },
              {
                title: "Expert Team",
                desc: "Experienced professionals across all event aspects",
              },
              {
                title: "Vendor Network",
                desc: "Access to trusted partners and premium suppliers",
              },
              {
                title: "Stress-Free Experience",
                desc: "We handle all logistics so you can enjoy your event",
              },
            ].map((item, index) => (
              <Box key={index} sx={{ display: "flex", mb: 3 }}>
                <Box
                  sx={{
                    minWidth: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: alpha("#6a11cb", 0.1),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: 2,
                    color: "primary.main",
                    fontWeight: 600,
                  }}
                >
                  {index + 1}
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            ))}

            <Alert severity="info" sx={{ mt: 4, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={500}>
                Need immediate assistance? Call us at{" "}
                <strong> +91 85848 26222</strong> or email{" "}
                <strong>info@momentsia.com</strong>
              </Typography>
            </Alert>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Email sent successfully! We'll contact you within 24 hours."
      />
    </Box>
  );
};

export default ContactForm;
